<template>
  <sf-dialog v-model="internalModel" scrollable>
    <template #activator="{ props: activatorProps }">
      <slot name="activator" :props="activatorProps"></slot>
    </template>
    <v-card>
      <v-card-item>
        <v-card-title>
          <sf-heading>{{ $t('quotes.createRequest') }}</sf-heading>
        </v-card-title>
      </v-card-item>
      <v-card-text class="d-flex flex-column">
        <template v-if="quotesIncludingProduct.length > 0">
          <div
            class="d-flex flex-column pb-1 overflow-y-auto overflow-x-hidden scrollbar"
            style="max-height: 128px"
          >
            <div
              v-for="quoteIncludingProduct in quotesIncludingProduct"
              :key="quoteIncludingProduct.id"
              class="d-flex align-center"
            >
              <v-icon :icon="mdiCheck" color="green" class="mr-1" />
              <router-link
                :to="{ name: 'Quote', params: { id: quoteIncludingProduct.id } }"
                class="text-truncate text-primary text-decoration-none"
              >
                {{ quoteIncludingProduct.name }}
              </router-link>
            </div>
          </div>
          <v-divider class="my-1" />
        </template>
        <sf-text class="mb-1">
          {{ $t('quotes.addProductToExistingQuote') }}
        </sf-text>
        <v-autocomplete
          v-model="selectedQuotes"
          class="combobox-role"
          color="primary"
          variant="outlined"
          density="compact"
          clearable
          chips
          closable-chips
          multiple
          hide-details
          :items="quotesExcludingProduct"
          item-title="name"
          item-value="id"
          return-object
          :no-data-text="$t('quotes.noQuoteRequestsFound')"
        />
        <sf-text class="my-1">
          {{ $t('quotes.addProductToNewQuote') }}
        </sf-text>
        <sf-text-field v-model="newQuote" clearable hide-details autocomplete="off" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <sf-text-button
          color="primary"
          :disabled="(!selectedQuotes || selectedQuotes.length === 0) && !newQuote"
          @click="addProductToQuoteRequest"
        >
          {{ $t('save') }}
        </sf-text-button>
      </v-card-actions>
    </v-card>
  </sf-dialog>
</template>

<script setup lang="ts">
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfDialog from '@/components/dialog/SfDialog.vue'
import SfTextField from '@/components/input/SfTextField.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import SfText from '@/components/text/SfText.vue'
import useAddToQuote from '@/modules/quote/useAddToQuote'
import { Event } from '@/modules/tracking/types'
import { trackEvent } from '@/modules/tracking/useTracking'
import { QuoteRequestForProduct } from '@/types/index'
import { mdiCheck } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { PropType, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps({
  productId: {
    type: Number,
    required: true
  },
  addToQuoteEvent: {
    type: Object as PropType<Event>,
    required: true
  },
  createQuoteEvent: {
    type: Object as PropType<Event>,
    required: true
  }
})

const model = defineModel<boolean>({ default: false })
const internalModel = ref(false)

const { quotesIncludingProduct, quotesExcludingProduct } = storeToRefs(useAddToQuote())
const newQuote = ref<string | undefined>(undefined)
const selectedQuotes = ref<QuoteRequestForProduct[]>([])

watch(internalModel, (newValue) => {
  model.value = newValue
})

watch(model, async (newValue) => {
  if (newValue) {
    await useAddToQuote().getDraftQuotes(props.productId.toString())
    selectedQuotes.value = []
    newQuote.value = undefined
  }
  // open dialog only after data has finished loading to prevent pop-ins
  internalModel.value = newValue
})

const addProductToQuoteRequest = () => {
  if (newQuote.value) {
    useAddToQuote().addToNewQuoteRequest(newQuote.value, props.productId)
    trackEvent(props.createQuoteEvent.withProduct(props.productId))
  }
  if (selectedQuotes.value) {
    useAddToQuote().addToQuoteRequests(selectedQuotes.value, props.productId)
    trackEvent(props.addToQuoteEvent.withProduct(props.productId))
  }
  internalModel.value = false
}
</script>
